import { completeWorldpayAccessWithout3ds } from "../app-utils/worldpayAccess";
import Api from "../Service/api";
import PaymentAdd from "./PaymentAdd";
import PaymentSuccess from "./PaymentSuccess";
import PaymentFailure from "./PaymentFailure";
import { T } from "../app-utils/translator";
import { AutoShipContext, LoadingContext, ProfileContext } from "../App";
import { Radio, Checkbox, Input, Modal, Form, Row, Col, Button, Spin } from "antd";
import { AddCircleOutlineRounded } from "@material-ui/icons";
import React, { useState, useEffect, useContext, useRef } from "react";
import { currencyMapping, getCardImages } from "../app-utils/ExportData";
import { appNotification as notification } from "../app-utils/antdFeedback";
import { LeftOutlined, RightCircleOutlined } from '@ant-design/icons';
import moneyImage from '../assets/money.svg';

const api = new Api();

const normalizeCheckoutList = (value, primaryKey) => {
    if (!Array.isArray(value)) {
        return [];
    }
    return [...new Set(
        value
            .map((item) => {
                if (item && typeof item === 'object') {
                    return (
                        item?.[primaryKey] ??
                        item?.account_id ??
                        item?.accountId ??
                        item?.paymentInstrumentUuid ??
                        item?.midUuid ??
                        item?.uuid ??
                        item?.id
                    );
                }
                return item;
            })
            .filter((item) => item !== null && item !== undefined && item !== '')
            .map((item) => String(item))
    )];
};

const normalizeCheckoutSelection = (listValue, fallbackValue, primaryKey) => {
    const normalizedList = normalizeCheckoutList(listValue, primaryKey);
    if (normalizedList?.length > 0) {
        return normalizedList;
    }
    if (fallbackValue === null || fallbackValue === undefined || fallbackValue === '') {
        return [];
    }
    return [String(fallbackValue)];
};

const getCheckoutData = (value) => value?.data?.data ?? value?.data ?? value;

const PaymentList = () => {
    const [accounts, setAccounts] = useState([]);
    const [profileDataExternal, setProfileDataExternal] = useState();
    const [addNewPayment, setAddNewPayment] = useState();
    const [cardEditDetails, setCardEditDetails] = useState();
    const [paymentConnectors, setPaymentConnectors] = useState([]);
    const [showConfirmScreen, setShowConfirmScreen] = useState();
    const [paymentInstruments, setPaymentInstruments] = useState([]);
    const [checkoutParams, setCheckoutParams] = useState(null);
    const [paymentInstrumentConnectors, setPaymentInstrumentConnectors] = useState([]);
    const [autoshipReqParams, setAutoshipReqParams] = useState({ "currency": null, "paymentInstrumentUuId": null, "requestReference": null, "paymentChannelId": null, "clientCreditId": null, "maxClientCredit": null, "fiatAccountId": null, "fiatMaxAmount": null, "loyaltyAccountId": null, "loyaltyMaxAmount": null, "transferAcountId": null, "transferMaxAmount": null, "autoshipReference": null, "autoshipProvider": null });
    const [worldpayAccessModalOpen, setWorldpayAccessModalOpen] = useState(false);
    const [worldpayAccessModalStep, setWorldpayAccessModalStep] = useState('confirm');
    const [worldpayAccessCheckout, setWorldpayAccessCheckout] = useState(null);
    const [worldpayAccessReady, setWorldpayAccessReady] = useState(false);
    const [worldpayAccessLoading, setWorldpayAccessLoading] = useState(false);
    const [worldpayAccessSubmitError, setWorldpayAccessSubmitError] = useState('');
    const [worldpayAccessFieldErrors, setWorldpayAccessFieldErrors] = useState({
        pan: false,
        expiry: false,
        cvv: false
    });
    const [worldpayAccessForm] = Form.useForm();
    const worldpayAccessCleanupRef = useRef(() => { });
    const worldpayAccessActiveRef = useRef(false);
    const worldpayAccessInitTimerRef = useRef(null);
    const worldpayAccessChallengeResolveRef = useRef(null);
    const worldpayAccessChallengeRejectRef = useRef(null);
    const worldpayAccessPendingAutoshipReqParamsRef = useRef(null);

    const { setLoading } = useContext(LoadingContext);
    const { profileData } = useContext(ProfileContext);
    const { autoShipDetails, setAutoShipDetails } = useContext(AutoShipContext);

    useEffect(() => {
        validateParams();
        setAutoshipReqParams((autoshipReqParams) => ({ ...autoshipReqParams, 'currency': autoShipDetails?.currency, 'requestReference': autoShipDetails?.requestReference, autoshipExternalId: autoShipDetails?.autoshipExternalId, autoshipReference: autoShipDetails?.autoshipReference, autoshipProvider: Number(autoShipDetails?.autoshipProvider), language: autoShipDetails?.language }));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (profileData?.invalidDomainError) {
            setAutoShipDetails((autoShipDetails) => ({ ...autoShipDetails, errorMessage: 'Access Denied!' }));
            setShowConfirmScreen('failure');
        }
    }, [profileData?.invalidDomainError, setAutoShipDetails]);

    const setErrorMessages = (message) => {
        setAutoShipDetails((autoShipDetails) => ({ ...autoShipDetails, errorMessage: message }));
        setShowConfirmScreen('failure');
    }

    const validateParams = async () => {
        setLoading(true);
        const url = 'external/validate/checkout-params';
        const result = await api.mainRestCallService(url, 'POST', autoShipDetails);
        setLoading(false);
        if (result?.status) {
            getProfileData();
            setCheckoutParams(result?.data);
        } else {
            setErrorMessages(((result === 401) ? T('SessionExpired') : T('InvalidRequest')));
            return;
        }
    };

    const getProfileData = async () => {
        let result = await api.mainRestCallService('external/profile', 'GET');
        if (result && result.status === 1) {
            setProfileDataExternal((profileDataExternal) => ({ ...profileDataExternal, ...result.data, hkcm: result?.data?.hkcm, 'distributorId': result?.data?.distributorId }));
        }
    }

    useEffect(() => {
        const checkoutData = getCheckoutData(checkoutParams);
        if (!checkoutData) {
            return;
        }

        const selectedAccountValues = new Set(normalizeCheckoutList(checkoutData?.accounts, 'accountId'));
        const selectedPaymentInstrumentValues = normalizeCheckoutSelection(checkoutData?.paymentInstruments, checkoutData?.paymentInstrumentUuid, 'paymentInstrumentUuid');
        const selectedPaymentConnectorValues = normalizeCheckoutSelection(checkoutData?.paymentConnectors, checkoutData?.midUuid, 'midUuid');

        setAutoshipReqParams((prev) => {
            const next = { ...prev };
            let hasChanges = false;

            if (selectedAccountValues?.size > 0 && accounts?.length > 0) {
                const fiatAccount = accounts?.find((account) =>
                    account?.accountType === 1 &&
                    (selectedAccountValues.has(String(account?.account_id)) || selectedAccountValues.has(String(account?.accountType)))
                );
                const loyaltyAccount = accounts?.find((account) =>
                    account?.accountType === 2 &&
                    (selectedAccountValues.has(String(account?.account_id)) || selectedAccountValues.has(String(account?.accountType)))
                );

                const transferAccount = accounts?.find((account) =>
                    account?.accountType === 3 &&
                    (selectedAccountValues.has(String(account?.account_id)) || selectedAccountValues.has(String(account?.accountType)))
                );

                const fiatAccountId = fiatAccount?.account_id ?? null;
                const loyaltyAccountId = loyaltyAccount?.account_id ?? null;
                const transferAcountId = transferAccount?.account_id ?? null;

                if ((next?.fiatAccountId ?? null) !== fiatAccountId) {
                    next.fiatAccountId = fiatAccountId;
                    hasChanges = true;
                }
                if ((next?.loyaltyAccountId ?? null) !== loyaltyAccountId) {
                    next.loyaltyAccountId = loyaltyAccountId;
                    hasChanges = true;
                }
                if ((next?.transferAcountId ?? null) !== transferAcountId) {
                    next.transferAcountId = transferAcountId;
                    hasChanges = true;
                }
            }
            

            const matchedConnector = selectedPaymentConnectorValues?.find((midUuid) =>
                paymentConnectors?.some((connector) => String(connector?.gatewayMidUuid) === String(midUuid))
            );
            const matchedPaymentInstrument = selectedPaymentInstrumentValues?.find((uuid) =>
                paymentInstruments?.some((instrument) => String(instrument?.uuid) === String(uuid))
            );

            if (matchedConnector) {
                if ((next?.gatewayMidUuid ?? null) !== matchedConnector) {
                    next.gatewayMidUuid = matchedConnector;
                    hasChanges = true;
                }
                if (next?.paymentInstrumentUuId !== null) {
                    next.paymentInstrumentUuId = null;
                    hasChanges = true;
                }
            } else if (matchedPaymentInstrument) {
                if ((next?.paymentInstrumentUuId ?? null) !== matchedPaymentInstrument) {
                    next.paymentInstrumentUuId = matchedPaymentInstrument;
                    hasChanges = true;
                }
                if (next?.gatewayMidUuid !== null) {
                    next.gatewayMidUuid = null;
                    hasChanges = true;
                }
            }

            return hasChanges ? next : prev;
        });
    }, [accounts, paymentConnectors, paymentInstruments, checkoutParams]);

    useEffect(() => {
        if (profileDataExternal?.distributorId) {
            getAccounts();
            getPaymentConnectors();
        }
        // eslint-disable-next-line
    }, [profileDataExternal]);

    useEffect(() => {
        return () => {
            worldpayAccessActiveRef.current = false;
            worldpayAccessCleanupRef.current?.();
            worldpayAccessChallengeResolveRef.current = null;
            worldpayAccessChallengeRejectRef.current = null;
            if (worldpayAccessInitTimerRef.current) {
                window.clearTimeout(worldpayAccessInitTimerRef.current);
            }
        };
    }, []);

    const getAccounts = async () => {
        setLoading(true);
        let url = '';
        if (autoShipDetails?.autoshipReference) {
            url = 'external/accounts?status=1'
        } else {
            url = 'external/accounts?status=1&currency=' + autoShipDetails?.currency;
        }
        const result = await api.mainRestCallService(url, 'GET');
        setLoading(false);
        if (result?.status) {
            const list = result?.data.filter(data => (data.accountType !== 5 && data.accountType !== 4));
            setAccounts(list);
        } else if (result === 401) {
            setErrorMessages(T('SessionExpired'));
            return;
        } else {
            setAccounts([]);
        }
    }


    const getPaymentConnectors = async () => {
        setLoading(true);
        const url = 'external/connector?featureId=1&currency=' + autoShipDetails?.currency + '&marketUuid=' + autoShipDetails?.marketUuid;
        const result = await api.mainRestCallService(url, 'GET');
        setLoading(false);
        if (result?.status) {
            var filteredPaymentConnectors = result?.data?.filter(data => (data.paymentChannel === 'PAYQUICKER' || data.paymentChannel === 'ALIPAY' || data.paymentChannel === 'ALIPAY_HK' || data.paymentChannel === 'WECHAT_PAY' || data.paymentChannel === 'JKOPAY') || data.paymentChannel === 'UNION_PAY' || data.paymentChannel === 'UNION_PAY_CARD' || data.paymentChannel === 'PROMPTPAY' || data.paymentChannel === 'BANK_TRANSFER' || data.paymentChannel === 'CASH');
            // 4902 changes =========================================================================
            if ((autoShipDetails?.currency === 'HKD' && profileDataExternal?.hkcm)) {
                filteredPaymentConnectors = filteredPaymentConnectors?.filter(data => (data?.paymentChannel !== 'PAYQUICKER'));
            }
            // 4902 changes =========================================================================
            var isAchConnector = result.data.filter(connector => (connector.paymentChannel === 'ACH'));
            // MDAP-4467 ============================================================================
            if (isAchConnector) {
                const checkNmiAch = isAchConnector?.displayName === 'NMI_ACH';
                if (!checkNmiAch) {
                    isAchConnector = null;
                }
            }
            

            if (filteredPaymentConnectors.find(data => data.paymentChannel === "UNION_PAY")) {
                const unionPayConnectors = filteredPaymentConnectors.filter(data => data.paymentChannel === "UNION_PAY");
                const groupedUnionPay = {
                    ...unionPayConnectors[0],
                    paymentChannel: "UNION_PAY",
                    connectors: unionPayConnectors
                };
                filteredPaymentConnectors = filteredPaymentConnectors.filter(data => data.paymentChannel !== "UNION_PAY");
                filteredPaymentConnectors.push(groupedUnionPay);
            }

            if (filteredPaymentConnectors.find(data => data.paymentChannel === "ALIPAY")) {
                const alipayConnectors = filteredPaymentConnectors.filter(data => data.paymentChannel === "ALIPAY");
                const groupedAlipay = {
                    ...alipayConnectors[0],
                    paymentChannel: "ALIPAY",
                    connectors: alipayConnectors
                };
                filteredPaymentConnectors = filteredPaymentConnectors.filter(data => data.paymentChannel !== "ALIPAY");
                filteredPaymentConnectors.push(groupedAlipay);
            }

            // MDAP-4467 End=========================================================================
            const isCardConnectorPresent = result.data.filter(connector => (connector.paymentChannel === 'CARD' || connector.paymentChannel === 'SEPA'));
            var isCardConnector = (isCardConnectorPresent) ? JSON.parse(JSON.stringify(isCardConnectorPresent)) : isCardConnectorPresent;
            if (isAchConnector || isCardConnector) {
                const type = ((isAchConnector && isCardConnector) ? '' : (isAchConnector) ? 1 : '2,3');
                getPaymentInstruments(type);
            }
            setPaymentInstrumentConnectors((isAchConnector && isCardConnector) ? [...isCardConnector, ...isAchConnector] : (isAchConnector) ? [...isAchConnector] : (isCardConnector) && [...isCardConnector]);
            filteredPaymentConnectors = filteredPaymentConnectors.sort((a, b) => a?.displayOrder - b?.displayOrder);
            setPaymentConnectors((filteredPaymentConnectors) ? (filteredPaymentConnectors) : []);
        } else if (result === 401) {
            setErrorMessages(T('SessionExpired'));
            return;
        }
    };


    const getPaymentInstruments = async (type) => {
        setLoading(true);
        var url = 'external/payment-instrument?status=1&txnCurrency=' + autoShipDetails?.currency + '&instrument_type=' + (type);
        const result = await api.mainRestCallService(url, 'GET');
        setLoading(false);
        if (result?.status) {
            const instrumentResults = Array.isArray(result?.data)
                ? result.data
                : (result?.data?.results ?? []);
            const filteredPaymentInstruments = instrumentResults.filter(data => data.instrument_type === 2 || data.instrument_type === 3);
            setPaymentInstruments(filteredPaymentInstruments);
        } else if (result === 401) {
            setErrorMessages(T('SessionExpired'));
            return;
        }
    };

    const refreshPaymentList = () => {
        const isAchConnector = paymentInstrumentConnectors.find(connector => (connector.paymentChannelId === 2));
        const isCardConnector = paymentInstrumentConnectors.find(connector => (connector.paymentChannelId === 3));
        if (isAchConnector || isCardConnector) {
            const type = ((isAchConnector && isCardConnector) ? '' : (isAchConnector) ? 1 : 2);
            getPaymentInstruments(type);
        }
        setCardEditDetails();
        setAddNewPayment(false);
    }
    

    // onSelecting Radio for payment Channel and payment Instrument -------------------
    const onPaymentChange = (value, type) => {
        if (type === 'paymentInst') {
            const connectorId = paymentInstruments.find(payment => payment.uuid === value)?.display_name;
            setAutoshipReqParams((autoshipReqParams) => ({ ...autoshipReqParams, 'paymentInstrumentUuId': value, 'gatewayMidUuid': null, 'paymentChannelId': null, 'connectorId': connectorId }));
        } else if (type === 'paymentChannel') {
            setAutoshipReqParams((autoshipReqParams) => ({ ...autoshipReqParams, 'paymentInstrumentUuId': null, 'paymentChannelId': null, 'gatewayMidUuid': value }))
        }
    }


    // onSelecting or delecting checkbox for credit, uwallet and uwallet funding -------
    const onWalletChange = (value, type, event) => {
        if (event) {
            setAutoshipReqParams((autoshipReqParams) => ({ ...autoshipReqParams, [type]: (event.target.checked) ? value : null }))
        } else {
            setAutoshipReqParams((autoshipReqParams) => ({ ...autoshipReqParams, [type]: (value) ? (value) : null }))
        }
    }

    const getSelectedPaymentInstrument = () => {
        return paymentInstruments?.find((instrument) => String(instrument?.uuid) === String(autoshipReqParams?.paymentInstrumentUuId));
    }

    const hasWorldpayAccessConnector = () => {
        return paymentInstrumentConnectors?.some((connector) => (
            connector?.displayName === 'WORLDPAY_ACCESS' ||
            connector?.paymentChannel === 'WORLDPAY_ACCESS'
        ));
    }

    const shouldRequireWorldpayAccessReconfirm = () => {
        return hasWorldpayAccessConnector() && getSelectedPaymentInstrument()?.targetGateway === 'WORLDPAY_ACCESS';
    }

    const getWorldpayAccessCheckoutIdentity = () => {
        return process.env.REACT_APP_WORLDPAY_ACCESS_CHECKOUT_ID || '';
    };

    const getWorldpayAccessConnector = () => {
        return paymentInstrumentConnectors?.find((connector) => connector?.displayName === 'WORLDPAY_ACCESS');
    }

    const resetWorldpayAccessModalState = (isModalClosed = false) => {
        if (!isModalClosed) {
            getPaymentInstruments(2);
        }
        worldpayAccessCleanupRef.current?.();
        worldpayAccessCleanupRef.current = () => { };
        worldpayAccessChallengeResolveRef.current = null;
        worldpayAccessChallengeRejectRef.current = null;
        worldpayAccessPendingAutoshipReqParamsRef.current = null;
        if (worldpayAccessInitTimerRef.current) {
            window.clearTimeout(worldpayAccessInitTimerRef.current);
            worldpayAccessInitTimerRef.current = null;
        }
        setWorldpayAccessCheckout(null);
        setWorldpayAccessReady(false);
        setWorldpayAccessLoading(false);
        setWorldpayAccessSubmitError('');
        setWorldpayAccessFieldErrors({
            pan: false,
            expiry: false,
            cvv: false
        });
        worldpayAccessForm.resetFields();
    }

    const closeWorldpayAccessModal = () => {
        worldpayAccessActiveRef.current = false;
        if (worldpayAccessChallengeRejectRef.current) {
            worldpayAccessChallengeRejectRef.current(new Error('WORLD_PAY_ACCESS_CANCELLED'));
        }
        setWorldpayAccessModalOpen(false);
        setWorldpayAccessModalStep('confirm');
        resetWorldpayAccessModalState(true);
    }

    const removeSelectedWorldpayAccessPaymentInstrument = () => {
        const selectedPaymentInstrumentUuid = autoshipReqParams?.paymentInstrumentUuId;
        const selectedPaymentInstrument = paymentInstruments?.find((instrument) => (
            String(instrument?.uuid) === String(selectedPaymentInstrumentUuid)
        ));

        if (!selectedPaymentInstrumentUuid || selectedPaymentInstrument?.targetGateway !== 'WORLDPAY_ACCESS') {
            return;
        }

        setPaymentInstruments((prevPaymentInstruments) => (
            prevPaymentInstruments?.filter((instrument) => (
                String(instrument?.uuid) !== String(selectedPaymentInstrumentUuid)
            )) ?? []
        ));
        setAutoshipReqParams((prev) => ({
            ...prev,
            paymentInstrumentUuId: null,
            gatewayMidUuid: null,
            paymentChannelId: null,
            connectorId: null
        }));
    };

    const openWorldpayAccessModal = () => {
        worldpayAccessActiveRef.current = true;
        resetWorldpayAccessModalState();
        setWorldpayAccessModalStep('confirm');
        setWorldpayAccessModalOpen(true);
    }

    const initWorldpayAccess = async () => {
        if (!worldpayAccessActiveRef.current || typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const currentForm = document.getElementById('card-form');
        const currentPan = document.getElementById('card-pan');
        const currentExpiry = document.getElementById('card-expiry');
        const currentCvv = document.getElementById('card-cvv');

        if (!currentForm || !currentPan || !currentExpiry || !currentCvv) {
            worldpayAccessInitTimerRef.current = window.setTimeout(() => {
                if (worldpayAccessActiveRef.current) {
                    initWorldpayAccess();
                }
            }, 100);
            return;
        }

        if (typeof window.initWorldpayAccessCheckout !== 'function') {
            notification.error({ message: 'Error', description: 'WP Access checkout is not available.' });
            return;
        }

        const connector = getWorldpayAccessConnector();
        const checkoutIdentity = getWorldpayAccessCheckoutIdentity();
        const connectorIdentity = connector?.gatewayMerchantId;

        if (!connectorIdentity && !checkoutIdentity) {
            setWorldpayAccessSubmitError('WP Access checkout identity is missing.');
            notification.error({ message: 'Error', description: 'WP Access checkout identity is missing.' });
            return;
        }

        try {
            setWorldpayAccessLoading(true);
            setWorldpayAccessReady(false);
            setWorldpayAccessSubmitError('');
            setWorldpayAccessFieldErrors({
                pan: false,
                expiry: false,
                cvv: false
            });

            const checkout = await window.initWorldpayAccessCheckout({
                id: connectorIdentity || checkoutIdentity
            });

            setWorldpayAccessCheckout(checkout);
            setWorldpayAccessReady(true);

            const handleFieldChange = (event) => {
                const fieldName = event?.detail?.field?.name;
                const isValid = event?.detail?.['is-valid'];

                if (!fieldName || !['pan', 'expiry', 'cvv'].includes(fieldName)) {
                    return;
                }

                setWorldpayAccessFieldErrors((prev) => ({
                    ...prev,
                    [fieldName]: isValid === false
                }));
                setWorldpayAccessSubmitError('');
            };

            const handleFormReady = () => {
                setWorldpayAccessReady(true);
            };

            currentForm.addEventListener('wp:field:change', handleFieldChange);
            currentForm.addEventListener('wp:form:ready', handleFormReady);

            worldpayAccessCleanupRef.current = () => {
                currentForm.removeEventListener('wp:field:change', handleFieldChange);
                currentForm.removeEventListener('wp:form:ready', handleFormReady);
            };
        } catch (error) {
            const errorMessage = error?.message || 'WP Access checkout failed to initialize.';
            setWorldpayAccessSubmitError(errorMessage);
            notification.error({ message: 'Error', description: errorMessage });
            setWorldpayAccessReady(false);
        } finally {
            setWorldpayAccessLoading(false);
        }
    }

    const handleWorldpayAccessConfirmCancel = () => {
        removeSelectedWorldpayAccessPaymentInstrument();
        closeWorldpayAccessModal();
    }

    const handleWorldpayAccessConfirmContinue = () => {
        setWorldpayAccessModalStep('form');
        window.setTimeout(() => {
            if (worldpayAccessActiveRef.current) {
                initWorldpayAccess();
            }
        }, 0);
    }

    const submitWorldpayAccessCollectionForm = (collection) => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return Promise.reject(new Error('DOCUMENT_NOT_AVAILABLE'));
        }

        return new Promise((resolve, reject) => {
            const mountTarget = document.body || document.documentElement;
            if (!mountTarget) {
                reject(new Error('DOCUMENT_BODY_NOT_AVAILABLE'));
                return;
            }

            const iframeName = `autoship-worldpay-access-${Math.random().toString(36).slice(2)}`;
            const iframe = document.createElement('iframe');
            iframe.name = iframeName;
            iframe.id = iframeName;
            iframe.style.display = 'none';

            const cleanup = () => {
                window.removeEventListener('message', messageHandler);
                iframe.remove();
            };

            const resolveWithReference = (gatewayReference) => {
                cleanup();
                resolve(gatewayReference);
            };

            const messageHandler = (event) => {
                const eventData = typeof event?.data === 'string'
                    ? (() => {
                        try {
                            return JSON.parse(event.data);
                        } catch (parseError) {
                            return null;
                        }
                    })()
                    : event?.data;

                if (event?.origin !== collection.origin) {
                    return;
                }

                const gatewayReference = eventData?.SessionId || eventData?.sessionId || eventData?.message?.SessionId;
                if (gatewayReference) {
                    resolveWithReference(gatewayReference);
                }
            };

            mountTarget.appendChild(iframe);
            window.addEventListener('message', messageHandler);

            iframe.srcdoc = `
                <!DOCTYPE html>
                <html>
                <head>
                  <title>3DS Device Data Collection</title>
                </head>
                <body>
                  <form
                    id="collectionForm"
                    name="devicedata"
                    method="POST"
                    action="${collection.url}">
                    <input type="hidden" name="Bin" value="${collection.gatewayKey}">
                    <input type="hidden" name="JWT" value="${collection.gatewayToken}">
                  </form>
                  <script>
                    window.onload = function () {
                      document.getElementById('collectionForm').submit();
                    };
                  </script>
                </body>
                </html>
            `;
        });
    };

    const submitWorldpayAccessChallengeForm = async (challengeUrl, gatewayToken, targetId = '3d-secure-verification') => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            throw new Error('DOCUMENT_NOT_AVAILABLE');
        }

        const challengeOrigin = new URL(challengeUrl).origin;

        return new Promise((resolve, reject) => {
            let retryCount = 0;
            const maxRetries = 20;

            const cleanup = () => {
                window.removeEventListener('message', messageHandler);
            };

            const messageHandler = (event) => {
                if (event?.origin !== challengeOrigin) {
                    return;
                }

                const eventData = typeof event?.data === 'string'
                    ? (() => {
                        try {
                            return JSON.parse(event.data);
                        } catch (parseError) {
                            return null;
                        }
                    })()
                    : event?.data;

                const sessionId = eventData?.SessionId || eventData?.sessionId;
                const isCompleted = eventData?.MessageType === 'profile.completed' || eventData?.messageType === 'profile.completed';
                if (isCompleted && sessionId) {
                    cleanup();
                    resolve(sessionId);
                }
            };

            window.addEventListener('message', messageHandler);

            const mountChallenge = () => {
                const challengeFrame = document.getElementById(targetId);
                if (!challengeFrame || challengeFrame.tagName !== 'IFRAME') {
                    if (retryCount < maxRetries) {
                        retryCount += 1;
                        window.setTimeout(mountChallenge, 100);
                        return;
                    }
                    cleanup();
                    reject(new Error('WORLD_PAY_CHALLENGE_CONTAINER_NOT_FOUND'));
                    return;
                }

                challengeFrame.srcdoc = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <title>3DS Challenge</title>
                      <style>
                        html, body {
                          margin: 0;
                          width: 100%;
                          height: 100%;
                          overflow: hidden;
                          background: #fff;
                        }
                      </style>
                    </head>
                    <body>
                      <form
                        id="challengeForm"
                        method="POST"
                        action="${challengeUrl}">
                        <input type="hidden" name="JWT" value="${gatewayToken}">
                      </form>
                      <script>
                        window.onload = function () {
                          document.getElementById('challengeForm').submit();
                        };
                      </script>
                    </body>
                    </html>
                `;
            };

            window.setTimeout(mountChallenge, 0);
        });
    };

    useEffect(() => {
        const handleWorldpayAccessStatus = (event) => {
            const eventData = event?.data;
            const status = typeof eventData === 'string'
                ? (eventData === 'card_status_check' ? 'SUCCESS' : '')
                : (eventData?.status || '');

            if (eventData === 'card_status_check' || eventData?.type === 'card_status_check') {
                if (!worldpayAccessChallengeResolveRef.current && !worldpayAccessChallengeRejectRef.current) {
                    return;
                }
                setLoading(true);
                setWorldpayAccessSubmitError('');
                setWorldpayAccessLoading(false);

                if (String(status).toUpperCase() === 'FAILED') {
                    if (worldpayAccessChallengeRejectRef.current) {
                        worldpayAccessChallengeRejectRef.current(new Error(T('cardAddFailText')));
                        worldpayAccessChallengeRejectRef.current = null;
                    }
                    setWorldpayAccessModalOpen(false);
                    setWorldpayAccessModalStep('confirm');
                    resetWorldpayAccessModalState();
                    setTimeout(() => {
                        notification.error({ message: 'Error', description: T('cardAddFailText') });
                    }, 0);
                    setLoading(false);
                    return;
                }

                if (worldpayAccessChallengeResolveRef.current) {
                    worldpayAccessChallengeResolveRef.current(true);
                    worldpayAccessChallengeResolveRef.current = null;
                }
                worldpayAccessChallengeRejectRef.current = null;
                refreshPaymentList?.();
                setAutoshipReqParams((prev) => ({
                    ...prev,
                    paymentInstrumentUuId: null,
                    gatewayMidUuid: null,
                    paymentChannelId: null,
                    connectorId: null
                }));
                setWorldpayAccessModalOpen(false);
                setWorldpayAccessModalStep('confirm');
                resetWorldpayAccessModalState();
                setLoading(false);
            }
        };

        window.addEventListener("message", handleWorldpayAccessStatus);
        return () => window.removeEventListener("message", handleWorldpayAccessStatus);
        // eslint-disable-next-line
    }, [setLoading]);

    const getWorldpayAccessRequestBody = (sessionReference) => {
        const connector = getWorldpayAccessConnector();
        return {
            issuerCode: profileData?.issuerCode,
            midUuid: connector?.gatewayMidUuid,
            currency: autoShipDetails?.currency,
            originalPiUuid: autoshipReqParams?.paymentInstrumentUuId,
            clientName: worldpayAccessForm.getFieldValue('clientName') || profileData?.first_name || profileData?.name || '',
            source: 4,
            sessionReference,
            deviceData: getThreeDSDeviceData(worldpayAccessForm.getFieldValue('clientName') || profileData?.first_name || profileData?.name || '')
        };
    }

    const getThreeDSDeviceData = (clientName) => {
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const acceptHeader = typeof navigator !== 'undefined'
            ? (navigator.language ?? navigator.userAgent ?? '')
            : '';
        const screenWidth = typeof window !== 'undefined' ? window.screen?.width : undefined;
        const screenHeight = typeof window !== 'undefined' ? window.screen?.height : undefined;

        return {
            acceptHeader,
            userAgentHeader: userAgent,
            browserScreenWidth: `${screenWidth ?? ''}`,
            browserScreenHeight: `${screenHeight ?? ''}`,
            clientName: clientName ?? '',
        };
    };

    const handleWorldpayAccessSubmit = async (event) => {
        event.preventDefault();

        if (!worldpayAccessCheckout) {
            notification.error({ message: 'Error', description: 'WP Access checkout is still loading.' });
            return;
        }

        const hasValidationError = Object.values(worldpayAccessFieldErrors).some(Boolean);
        if (hasValidationError) {
            const errorMessage = 'Please enter valid card details.';
            setWorldpayAccessSubmitError(errorMessage);
            notification.error({ message: 'Error', description: errorMessage });
            return;
        }

        try {
            const is3DSecureEnabled = paymentInstrumentConnectors.find(channel => channel?.displayName === 'WORLDPAY_ACCESS')?.is3DSEnabled;
            setWorldpayAccessLoading(true);
            setWorldpayAccessSubmitError('');
            worldpayAccessPendingAutoshipReqParamsRef.current = {
                ...autoshipReqParams
            };
            const sessionState = await window.generateWorldpayAccessSessionState();
            const requestBody = getWorldpayAccessRequestBody(sessionState);
            const result = await api.mainRestCallService('pgapi/initiate-payment-instrument', 'POST', requestBody);
            if (result?.status === 1 && !result?.data?.url && !result?.data?.gatewayKey && !result?.data?.gatewayToken) {
                setWorldpayAccessLoading(false);
                setWorldpayAccessModalOpen(false);
                resetWorldpayAccessModalState();
                onPaymentChange(null, 'paymentInst')
                setLoading(false);
                refreshPaymentList?.();
                return;
            }
            if (result?.status !== 1 || !result?.data?.url || !result?.data?.gatewayKey || !result?.data?.gatewayToken) {
                throw new Error(T('failedAddCard'));
            }

            if (is3DSecureEnabled) {
                const ddcData = {
                    url: result.data.url,
                    gatewayKey: result.data.gatewayKey,
                    gatewayToken: result.data.gatewayToken,
                    reference: result.data.reference,
                    origin: new URL(result.data.url).origin
                };

                const gatewayReference = await submitWorldpayAccessCollectionForm(ddcData);
                const sessionId = String(gatewayReference || '').trim();
                if (!sessionId) {
                    throw new Error('WP Access session id is missing.');
                }

                const finalResult = await api.mainRestCallService('pgapi/initiate-pi-payment', 'POST', {
                    paymentId: sessionId,
                    reference: ddcData.reference
                });

                if (completeWorldpayAccessWithout3ds(finalResult, T('failedAddCard'))) {
                    closeWorldpayAccessModal();
                    onPaymentChange(null, 'paymentInst');
                    refreshPaymentList?.();
                    notification.success({ message: T('cardAddSuccess') });
                    return;
                }

                if (finalResult?.status !== 1 || !finalResult?.data?.url || !finalResult?.data?.gatewayToken) {
                    throw new Error(T('failedAddCard'));
                }

                setWorldpayAccessModalStep('challenge');
                worldpayAccessChallengeResolveRef.current = null;
                worldpayAccessChallengeRejectRef.current = null;
                const challengeCompletion = new Promise((resolve, reject) => {
                    worldpayAccessChallengeResolveRef.current = resolve;
                    worldpayAccessChallengeRejectRef.current = reject;
                });

                window.setTimeout(() => {
                    submitWorldpayAccessChallengeForm(
                        finalResult.data.url,
                        finalResult.data.gatewayToken,
                        '3d-secure-verification'
                    );
                }, 0);

                await challengeCompletion;
                closeWorldpayAccessModal();
            }


        } catch (error) {
            const errorMessage = error?.message || 'Please enter valid card details.';
            if (errorMessage !== 'WORLD_PAY_ACCESS_CANCELLED') {
                setWorldpayAccessSubmitError(errorMessage);
                notification.error({ message: 'Error', description: errorMessage });
            }
        } finally {
            setWorldpayAccessLoading(false);
            setLoading(false);
        }
    }

    const submitAutoShipConfigCore = async (reqParams = autoshipReqParams) => {

        if ((reqParams?.fiatAccountId && !reqParams?.fiatMaxAmount) || (!(/^\d+(\.\d{1,2})?$/.test(reqParams?.fiatMaxAmount)) && reqParams?.fiatMaxAmount)) {
            notification.error({ message: 'Error', description: T('uwalletAmountCheckText') });
            return;
        }
        if ((reqParams?.loyaltyAccountId && !reqParams?.loyaltyMaxAmount) || (!(/^\d+(\.\d{1,2})?$/.test(reqParams?.loyaltyMaxAmount)) && reqParams?.loyaltyMaxAmount)) {
            notification.error({ message: 'Error', description: T('uwalletFundAmountCheckText') });
            return;
        }
        if ((reqParams?.transferAcountId && !reqParams?.transferMaxAmount) || (!(/^\d+(\.\d{1,2})?$/.test(reqParams?.transferMaxAmount)) && reqParams?.transferMaxAmount)) {
            notification.error({ message: 'Error', description: T('uwalletTransferAmountCheckText') });
            return;
        }

        if (!reqParams?.clientCreditId && !reqParams?.fiatAccountId && !reqParams?.loyaltyAccountId && !reqParams.transferAcountId && !reqParams?.paymentInstrumentUuId && !reqParams?.gatewayMidUuid) {
            notification.error({ message: 'Error', description: T('paymentMethodErrorText') });
            return;
        }

        if (profileDataExternal?.requirePaymentMethod && !reqParams?.paymentInstrumentUuId && !reqParams?.gatewayMidUuid) {
            notification.error({ message: 'Error', description: T('autoshipError') });
            return;
        }

        // For world Pay channels -------------------
        const requestBody = { ...reqParams };
        if (!requestBody?.paymentInstrumentUuId) {
            const selectedChannel = paymentConnectors.find(channel => channel?.gatewayMidUuid === requestBody?.gatewayMidUuid);
            requestBody['midUuid'] = selectedChannel?.gatewayMidUuid;
            requestBody['paymentChannelId'] = (selectedChannel?.duplicateChannel) ? (selectedChannel?.originalChannelId) : requestBody['paymentChannelId'];
        }
        // For world Pay channels -------------------
        const url = 'external/autoship-order-config';
        const result = await api.mainRestCallService(url, 'POST', requestBody);
        if (result?.status) {
            setShowConfirmScreen('success');
        } else {
            setErrorMessages(((result === 401) ? T('SessionExpired') : T('autoshipFailTxt')));
            return;
        }
    }

    const submitAutoShipConfig = async () => {
        if (shouldRequireWorldpayAccessReconfirm()) {
            openWorldpayAccessModal();
            return;
        }

        await submitAutoShipConfigCore();
    }

    const redirectToMerchant = () => {
        var url = (autoShipDetails?.redirectUrl) + ((autoShipDetails?.redirectUrl.indexOf('?') >= 0) ? '&' : '?') + 'status=CANCELLED';
        window.open(url, "_self");
    }

    const editCard = (paymentObj) => {
        setCardEditDetails(paymentObj);
        setAddNewPayment(true);
    }

    const canEditPaymentInstrument = (paymentInstrument) => {
        if (paymentInstrument?.status !== 1 || !paymentInstrument?.gw_payment_instrument_id) {
            return false;
        }

        return (
            paymentInstrument?.display_name === 'WORLDPAY' ||
            paymentInstrument?.display_name === 'WORLDPAY_ACCESS' ||
            paymentInstrument?.targetGateway === 'WORLDPAY_ACCESS'
        );
    }

    const getCitconDisplayName = (channel) => {
        switch (channel) {
            case 'CARD':
                return 'CARD';
            case 'UNION_PAY':
                return 'Union Pay';
            case 'ALIPAY':
                return 'Alipay';
            case 'ALIPAY_HK':
                return 'paybyAlipayHK';
            case 'JKOPAY':
                return 'paybyJkoPay';
            case 'PROMPTPAY':
                return 'promptPay';
            case 'BANK_TRANSFER':
                return 'bankTransferThailand';
            case 'CASH':
                return 'paybyBankOrOnSite';
            case 'UNION_PAY_CARD':
                return 'unionPayCard';
            default:
                return 'addPaybyCard';
        }
    }

    const walletMap = {
        1: T('uwallet'),
        2: T('uwalletFund'),
        3: T('uwalletTransfer'),
    };

    return (
        <>
            {
                (!showConfirmScreen && !addNewPayment) &&
                <div className="">
                    <div className="">
                        <div className={'d-flex align-items-center justify-content-between mt-4'}>
                            <h6 className="mb-0"><b>{T('payment_instrument_list')}</b></h6>
                        </div>
                        {
                            accounts?.map((account, index) => (
                                <div key={index} className="cardOverlay mt-2">
                                    <div className="d-flex align-items-center justify-content-between h35">
                                        <div>
                                            <h6 className="letterSpace_2 mb-0" style={{ fontSize: '12px', lineHeight: 1.7 }}> {walletMap[account?.accountType]} </h6>
                                            <small>{T('current_balance')}: {currencyMapping(account?.currency)}{account?.available_balance.toFixed(2) ?? '0.00'}</small>
                                        </div>
                                        {(account?.accountType === 1) && <div> <Checkbox checked={autoshipReqParams?.fiatAccountId === account?.account_id} onChange={($event) => onWalletChange(account?.account_id, 'fiatAccountId', $event)} /> </div>}
                                        {(account?.accountType === 2) && <div> <Checkbox checked={autoshipReqParams?.loyaltyAccountId === account?.account_id} onChange={($event) => onWalletChange(account?.account_id, 'loyaltyAccountId', $event)} /> </div>}
                                        {(account?.accountType === 3) && <div> <Checkbox checked={autoshipReqParams?.transferAcountId === account?.account_id} onChange={($event) => onWalletChange(account?.account_id, 'transferAcountId', $event)} /> </div>}
                                    </div>
                                    {
                                        (account?.accountType === 1 && autoshipReqParams?.fiatAccountId === account?.account_id) &&
                                        <div className="mt-2">
                                            <Input value={autoshipReqParams?.fiatMaxAmount} onInput={($event) => onWalletChange($event.target.value, 'fiatMaxAmount', null)} placeholder={T('max_amount')} />
                                            {
                                                ((!(/^\d+(\.\d{1,2})?$/.test(autoshipReqParams?.fiatMaxAmount)) && autoshipReqParams?.fiatMaxAmount)) &&
                                                <small className="input-label-error">{T('validAmountText')}</small>
                                            }
                                        </div>
                                    }
                                    {
                                        (account?.accountType === 2 && autoshipReqParams?.loyaltyAccountId === account?.account_id) &&
                                        <div className="mt-2">
                                            <Input value={autoshipReqParams?.loyaltyMaxAmount} onInput={($event) => onWalletChange($event.target.value, 'loyaltyMaxAmount', null)} placeholder={T('max_amount')} />
                                            {
                                                ((!(/^\d+(\.\d{1,2})?$/.test(autoshipReqParams?.loyaltyMaxAmount)) && autoshipReqParams?.loyaltyMaxAmount)) &&
                                                <small className="input-label-error">{T('validAmountText')}</small>
                                            }
                                        </div>
                                    }
                                    {
                                        (account?.accountType === 3 && autoshipReqParams?.transferAcountId === account?.account_id) &&
                                        <div className="mt-2">
                                            <Input value={autoshipReqParams?.transferMaxAmount} onInput={($event) => onWalletChange($event.target.value, 'transferMaxAmount', null)} placeholder={T('max_amount')} />
                                            {
                                                ((!(/^\d+(\.\d{1,2})?$/.test(autoshipReqParams?.transferMaxAmount)) && autoshipReqParams?.transferMaxAmount)) &&
                                                <small className="input-label-error">{T('validAmountText')}</small>
                                            }
                                        </div>
                                    }
                                </div>
                            ))
                        }

                        {
                            (paymentConnectors?.length > 0 || paymentInstruments?.length > 0) &&
                            <div className={'d-flex align-items-center justify-content-between mt-4'}>
                                <h6 className="mb-0"><b>{T('payment_instruments')}</b></h6>
                                <h6 className="mb-0 link" onClick={() => onPaymentChange(null, 'paymentInst')}><b>{T('reset_text')}</b></h6>
                            </div>
                        }

                        {
                            (paymentInstruments?.length > 0 || paymentConnectors?.length > 0) &&
                            <Radio.Group value={(autoshipReqParams?.gatewayMidUuid) ?? (autoshipReqParams?.paymentInstrumentUuId)}>
                                {
                                    (paymentInstruments?.length > 0) && paymentInstruments.map((instmnt, index) =>
                                        <div className="mt-2" key={index}>
                                            <div className="cardOverlay">
                                                <div className="d-flex align-items-center justify-content-between h35">
                                                    <div className="d-flex align-items-center">
                                                        {(instmnt?.instrument_type === 2) && <img className={"card_img"} src={getCardImages(instmnt?.cardType)} alt={instmnt.cardType} />}
                                                        {(instmnt?.instrument_type === 1 || instmnt?.instrument_type === 3) && <img className={"card_img"} src={'https://images.vexels.com/media/users/3/263264/isolated/preview/11a26a1102ca38d370338cf6a91459c4-money-business-bank-icon.png'} alt={'Bank'} />}
                                                        <div className="nametext">
                                                            <h6 className="letterSpace_2 mb-0" style={{ fontSize: '12px' }}>
                                                                {(instmnt?.account_name) && <span>{instmnt?.account_name}</span>}
                                                                {(instmnt?.instrument_type === 2) &&
                                                                    <>
                                                                        {'**** **** **** ' + instmnt.card_number_last4}
                                                                        <small> ({`${instmnt.exp_month}/${instmnt.exp_year}`})</small>
                                                                        {canEditPaymentInstrument(instmnt) && <span onClick={() => editCard(instmnt)} className="pl-2 text-primary" style={{ cursor: 'pointer' }}>{"Edit"}</span>}
                                                                    </>
                                                                }
                                                                {(instmnt?.instrument_type === 1) && '****' + instmnt.account_reference.slice(-4)}
                                                                {(instmnt?.instrument_type === 3) && instmnt?.account_reference}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Radio name="paymentInst" onClick={() => onPaymentChange(instmnt.uuid, 'paymentInst')} value={instmnt.uuid}></Radio>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }

                                {
                                    (paymentConnectors?.length > 0) && paymentConnectors.map((connector, index) =>
                                        <div className="mt-2" key={index}>
                                            <div className="cardOverlay">
                                                <div className="d-flex align-items-center justify-content-between h35">
                                                    <div className="d-flex align-items-center">
                                                        <img className={"card_img"} src={moneyImage} alt={connector?.displayName} />
                                                        <div className="nametext">
                                                            <h6 className="letterSpace_2 mb-0" style={{ fontSize: '12px', lineHeight: 1.7 }}>
                                                                {
                                                                    (connector?.displayName === 'CITCON_PAYMENT' || connector?.displayName === 'PROMPTPAY_VIA_WORLDPAY' || connector?.displayName === 'BANKTRANSFER_VIA_WORLDPAY' || connector?.displayName === 'CASH')
                                                                        ? T(getCitconDisplayName(connector?.paymentChannel))
                                                                        : ((T(connector?.paymentChannel.replace(/_/g, '').toLowerCase())[0]) === (connector?.paymentChannel.replace(/_/g, '').toLowerCase())) ? (connector?.paymentChannel.replace(/_/g, ' ')) : (T(connector?.paymentChannel.replace(/_/g, '').toLowerCase()))
                                                                }
                                                            </h6>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Radio onClick={() => onPaymentChange(connector?.gatewayMidUuid, 'paymentChannel')} value={connector?.gatewayMidUuid}></Radio>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                            </Radio.Group>
                        }

                        {
                            (paymentInstrumentConnectors?.length > 0) &&
                            <div className="cardOverlay mt-2" style={{ cursor: 'pointer' }}>
                                <div className="d-flex align-items-center justify-content-center p-1" onClick={() => setAddNewPayment(true)}>
                                    <AddCircleOutlineRounded /><h6 className="letterSpace_2 mb-0 pl-2" style={{ fontSize: '12px' }}>{T('bank_card_add')}</h6>
                                </div>
                            </div>
                        }

                        <div className="footer">
                            <button onClick={() => submitAutoShipConfig()} className="btn newCardbutton"> <LeftOutlined style={{ color: 'transparent' }} /> {T('submit_text')} <RightCircleOutlined /></button>
                            <small className="link" onClick={() => redirectToMerchant()}>{T('cancel_text')}</small>
                        </div>
                    </div>
                </div>
            }

            {(showConfirmScreen === 'success') && <PaymentSuccess />}

            {(showConfirmScreen === 'failure') && <PaymentFailure />}

            {(addNewPayment) && <PaymentAdd availableConnectors={paymentInstrumentConnectors} cardEditDetails={cardEditDetails} refreshPaymentList={refreshPaymentList} />}

            <Modal
                className="paymentlist-worldpay-modal"
                open={worldpayAccessModalOpen}
                onCancel={handleWorldpayAccessConfirmCancel}
                centered
                width={620}
                footer={null}
                destroyOnClose
                maskClosable={false}
            >
                {
                    (worldpayAccessModalStep === 'confirm') && (
                        <div>
                            <h5 className="mb-2"><b>Please reconfirm your card details</b></h5>
                            <p className="mb-0">Please reconfirm your card details</p>
                            <div className="d-flex align-items-center justify-content-end mt-4" style={{ gap: 12 }}>
                                <Button className="btn btn-danger" onClick={handleWorldpayAccessConfirmCancel}>
                                    {T('cancel_text')}
                                </Button>
                                <Button className="btn btn-primary" type="primary" onClick={handleWorldpayAccessConfirmContinue}>
                                    Continue
                                </Button>
                            </div>
                        </div>
                    )
                }

                {
                    (worldpayAccessModalStep === 'form') && (
                        <div>
                            <Form
                                id="card-form"
                                form={worldpayAccessForm}
                                layout="vertical"
                                style={{ width: '100%', marginTop: 12 }}
                                onSubmitCapture={handleWorldpayAccessSubmit}
                                initialValues={{ clientName: profileData?.first_name || profileData?.name || '' }}
                            >
                                <Col span={24} style={{ marginBottom: 20 }}>
                                    <label><span className="requiredField">*</span> {T('card_number')}</label>
                                    <div id="card-pan" className="worldpay-access-field worldpay-access-host" />
                                    {worldpayAccessFieldErrors.pan ? <p className="worldpay-access-error">{T('InvalidCardNumber')}</p> : null}
                                </Col>

                                <Col span={24} style={{ marginBottom: 20 }}>
                                    <Form.Item
                                        name="clientName"
                                        label={<><span className="requiredField">*</span> {T('cardName')}</>}
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                        rules={[
                                            { required: true, message: T('requiredError') },
                                            { pattern: /^[a-zA-Z\s-]{2,30}$/, message: T('Please enter a valid cardholder name') },
                                        ]}
                                    >
                                        <Input key="worldpay-card-holder" placeholder="Card holder's name" maxLength={30} autoComplete="off" />
                                    </Form.Item>
                                </Col>

                                <Col span={24} style={{ marginBottom: 20 }}>
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <label><span className="requiredField">*</span> {T('expiry_date')}</label>
                                            <div id="card-expiry" className="worldpay-access-field worldpay-access-host" />
                                            {worldpayAccessFieldErrors.expiry ? <p className="worldpay-access-error">{T('InvalidExpirationDate')}</p> : null}
                                        </Col>
                                        <Col span={12}>
                                            <label><span className="requiredField">*</span> {T('cvv')}</label>
                                            <div id="card-cvv" className="worldpay-access-field worldpay-access-host" />
                                            {worldpayAccessFieldErrors.cvv ? <p className="worldpay-access-error">{T('InvalidCVV')}</p> : null}
                                        </Col>
                                    </Row>
                                </Col>

                                {
                                    worldpayAccessSubmitError ? (
                                        <Col span={24} style={{ marginBottom: 12 }}>
                                            <p className="worldpay-access-error">{worldpayAccessSubmitError}</p>
                                        </Col>
                                    ) : null
                                }

                                <Col style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }} span={24}>
                                    <Button
                                        className="btn btn-danger"
                                        style={{ marginRight: 12, flex: 1 }}
                                        htmlType="button"
                                        onClick={handleWorldpayAccessConfirmCancel}
                                    >
                                        {T('cancel_text')}
                                    </Button>
                                    <button
                                        className="btn btn-primary"
                                        type="submit"
                                        disabled={!worldpayAccessReady || worldpayAccessLoading}
                                        style={{ flex: 1, width: 'auto' }}
                                    >
                                        <LeftOutlined style={{ color: 'transparent' }} /> {T('submit_text')} <RightCircleOutlined />
                                    </button>
                                </Col>
                            </Form>

                            {!worldpayAccessReady ? (
                                <div className="worldpay-access-loading">
                                    <Spin tip="Loading WP Access..." size="large" />
                                </div>
                            ) : null}
                        </div>
                    )
                }

                {
                    (worldpayAccessModalStep === 'challenge') && (
                        <div style={{ width: '100%', minHeight: 500 }}>
                            <iframe
                                id="3d-secure-verification"
                                name="3d-secure-verification"
                                style={{ width: '100%', height: '600px', border: 'none' }}
                                title="3D Secure Verification"
                            />
                        </div>
                    )
                }
            </Modal>
        </>
    )
}

export default PaymentList;
